import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { authApi } from "@/api/auth";
import { useSession } from "@/stores/session";
import { Button } from "@/components/ui/button";
import { MailWarning } from "lucide-react";

/**
 * Shown across the top of the app while the signed-in user's email is
 * unverified. Verification is a soft gate — you can use the app, but some
 * actions (e.g. upgrading to a paid plan) require a verified email.
 */
export function VerifyEmailBanner() {
  const user = useSession((s) => s.user);
  const [sent, setSent] = useState(false);
  const resend = useMutation({
    mutationFn: () => authApi.resendVerification(),
    onSuccess: () => setSent(true),
  });

  // Only show when we positively know the email is unverified.
  if (!user || user.emailVerified !== false) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-wait/30 bg-wait/10 px-6 py-2 text-[13px] text-wait">
      <MailWarning size={15} className="shrink-0" />
      <span className="text-foreground/90">
        Verifica tu correo (<span className="font-medium">{user.email}</span>) para desbloquear todas las funciones, como pasar a un plan de pago.
      </span>
      {sent ? (
        <span className="text-ok">Correo de verificación reenviado.</span>
      ) : (
        <span className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-wait hover:text-wait"
            disabled={resend.isPending}
            onClick={() => resend.mutate()}
          >
            {resend.isPending ? "Enviando…" : resend.isError ? "Reintentar" : "Reenviar correo"}
          </Button>
          {resend.isError && (
            <span className="text-fail">No pudimos enviar el correo. Inténtalo de nuevo.</span>
          )}
        </span>
      )}
    </div>
  );
}
